import React, { useEffect, useMemo, useState } from "react"
import {
  Container,
  Row,
  Col,
  Table,
  Card,
  CardBody,
  Modal,
  Form,
  Label,
  Input,
} from "reactstrap"
import { formatDate } from "utils/formatDate"
import NewPagination from "components/Common/Newpagination"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useGlobalFilter, usePagination, useTable } from "react-table"
import { Link, useNavigate } from "react-router-dom"
import configure from "configure"
import Select from "react-select"
import { useSelector } from "react-redux"
import Swal from "sweetalert2"
import { fetchNodeData, FetchNodePostData, FetchNodePutData, FetchNodeDeleteData, fetchData } from "utils/common-fetchData"
import secureLocalStorage from "react-secure-storage"
import Loader from "pages/Separate/Loader"
import axios from "axios"
import AccessDeniedPopup from "routes/AccessDeniedPopup"

const TEST_MODULE_ID = 121
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

// XOR-encodes a numeric test ID into a 16-char opaque token.
// Last 8 chars = hex(id ^ SECRET); first 8 = random alphanumeric.
const _TOKEN_SECRET = 1513948082
function encodeTestId(id) {
  const xored = (Number(id) ^ _TOKEN_SECRET) >>> 0
  const hexPart = xored.toString(16).padStart(8, "0")
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789"
  const randPart = Array.from({ length: 8 }, () =>
    chars[Math.floor(Math.random() * chars.length)]
  ).join("")
  return randPart + hexPart
}

const EMPTY_FORM = {
  id: null,
  title: "",
  category: [],
  question_count: "",
  date: "",
  duration: "",
  type: 1,
  assignees: [],
  test_type: 0,            // 0=actual  1=mock
  assign_type: "internal", // 'internal' | 'external' | 'department'
  departments: [],
  roles: [],
  is_recurring: 0,
  recurring_day: [],
  visible_date: "",
}

function ExamModal({ modal, setModal, onSubmit, selectedData = null, Categories, Departments, Roles }) {
  const isEdit = Boolean(selectedData)
  const [staffOptions , setStaffOptions] = useState([])
  const [formData, setFormData] = React.useState(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [ukStaffOptions, setUkStaffOptions] = useState([])

  useEffect(() => {
    getIndiaUsers()
    getUkUsers()
  }, [])

  async function getIndiaUsers() {
    const token = secureLocalStorage.getItem("auth_token")
    const APIdata = await axios.get(`${configure.apiUrl}/staff`, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    })
    if (APIdata?.data?.success) {
      const IndiaUsers = (APIdata.data.data || []).filter(item => item?.login_access === 1).map(u => ({
        value: u.id,
        label: u.alias_name || u.name,
      }))
      setStaffOptions(IndiaUsers)
    }
  }

  async function getUkUsers() {
    const token = secureLocalStorage.getItem("auth_token")
    const APIdata = await axios.get(`${configure.apiUkurl}/staff`, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    })
    if (APIdata?.data?.success) {
      const ukUsers = (APIdata.data.data || []).filter(item => item?.login_access === 1).map(u => ({
        value: u.id,
        label: u.alias_name || u.name,
      }))
      setUkStaffOptions(ukUsers)
    }
  }

  const allStaffOptions = useMemo(() => {
    const existingIds = new Set((staffOptions || []).map(s => String(s.value)))
    const newUkUsers = ukStaffOptions.filter(u => !existingIds.has(String(u.value)))
    return [...(staffOptions || []), ...newUkUsers]
  }, [staffOptions, ukStaffOptions])

  useEffect(() => {
    if (isEdit && selectedData) {
      const assignType =
        selectedData.assign_type ||
        (Number(selectedData.type) === 1 ? "internal" : "external")
      setFormData({
        id: selectedData.id || "",
        title: selectedData.test_name || "",
        category: selectedData.category || [],
        question_count: selectedData.question_count || "",
        date: selectedData.date || "",
        duration: selectedData.duration || "",
        type: selectedData.type ?? 1,
        assignees:
          selectedData.assign
            ?.split(",")
            ?.map(v => staffOptions?.find(s => s.value == v))
            ?.filter(Boolean) || [],
        test_type: selectedData.test_type ?? 0,
        assign_type: assignType,
        departments: selectedData.departments
          ? selectedData.departments
            .split(",")
            .map(v => Departments?.find(d => d.value == v))
            .filter(Boolean)
          : [],
        roles: selectedData.roles
          ? selectedData.roles
            .split(",")
            .map(v => Roles?.find(r => r.value == v))
            .filter(Boolean)
          : [],
        is_recurring: selectedData.is_recurring ?? 0,
        recurring_day: selectedData.recurring_day
          ? String(selectedData.recurring_day).split(",").map(Number)
          : [],
        visible_date: selectedData.visible_date || "",
      })
    }
    if (!modal) setFormData(EMPTY_FORM)
  }, [isEdit, selectedData, modal])

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleCategoryChange = (index, field, value) => {
    const updated = [...formData.category]
    updated[index] = { ...updated[index], [field]: value }
    setFormData({ ...formData, category: updated })
  }

  const totalQuestions = useMemo(
    () =>
      formData.category.reduce(
        (sum, item) =>
          sum + (Number(item.textInp) || 0) + (Number(item.mcq) || 0) + (Number(item.code) || 0),
        0
      ),
    [formData.category]
  )

  const pillBtn = (active, color) => ({
    flex: 1,
    border: "none",
    borderRadius: 6,
    padding: "5px 0",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    background: active ? (color === "mock" ? "#f59e0b" : "#752a97") : "transparent",
    color: active ? "#fff" : "#777",
    transition: "all 0.15s",
  })

  return (
    <Modal isOpen={modal} size="xl" centered>
      <div className="modal-header px-4 py-3">
        <h5 className="modal-title mt-0" style={{ fontSize: 15 }}>
          {isEdit ? "Edit Exam" : "Add Exam"}
        </h5>
        <button type="button" onClick={() => setModal(false)} className="btn-modal-close">
          <span aria-hidden="true" className="btn-modal-span">&times;</span>
        </button>
      </div>

      <div
        className="modal-body px-4 py-3"
        style={{ maxHeight: "82vh", overflowY: "auto", scrollbarWidth: "thin" }}
      >
        <Form
          onSubmit={e => {
            if (e) e.preventDefault()
            onSubmit(formData, totalQuestions, isEdit, setModal)
          }}
        >
          {/* ── Row 1: Title · Type pill ── */}
          <div className="row g-2 mb-2">
            <div className="col-md-9">
              <Label className="mb-1" style={{ fontSize: 12 }}>
                Exam Title <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter exam title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>Test Type</Label>
              <div
                style={{
                  display: "flex",
                  background: "#f3f4f6",
                  borderRadius: 8,
                  padding: 3,
                  gap: 3,
                }}
              >
                <button
                  type="button"
                  style={pillBtn(formData.test_type === 0, "actual")}
                  onClick={() => setFormData(prev => ({ ...prev, test_type: 0 }))}
                >
                  Actual
                </button>
                <button
                  type="button"
                  style={pillBtn(formData.test_type === 1, "mock")}
                  onClick={() => setFormData(prev => ({ ...prev, test_type: 1 }))}
                >
                  Mock
                </button>
              </div>
            </div>
          </div>

          {/* ── Categories ── */}
          <div className="mb-2">
            <Label className="mb-1" style={{ fontSize: 12 }}>
              Categories <span className="text-danger">*</span>
            </Label>
            <Select
              placeholder="Select categories…"
              className="basic-multi-select"
              classNamePrefix="select"
              options={Categories}
              value={formData.category}
              onChange={e => handleChange({ target: { name: "category", value: e } })}
              isMulti
              required
            />
          </div>

          {/* ── Per-category question counts ── */}
          {formData.category?.length ? (
            <div className="mb-2">
              <table className="table table-md table-bordered mb-1" style={{ fontSize: 12 }}>
                <thead className="table-light">
                  <tr>
                    <th>Category</th>
                    <th className="text-center" style={{ width: 150 }}>MCQ</th>
                    <th className="text-center" style={{ width: 150 }}>Text</th>
                    <th className="text-center" style={{ width: 150 }}>Code</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.category.map((item, index) => (
                    <tr key={item.label}>
                      <td className="py-1 align-middle">{item.label}</td>
                      {["mcq", "textInp", "code"].map(field => (
                        <td key={field} className="py-1">
                          <input
                            type="number"
                            min="0"
                            className="form-control form-control-sm text-center"
                            value={item[field] || ""}
                            onChange={e => handleCategoryChange(index, field, e.target.value)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 12, textAlign: "right", color: "#555" }}>
                Total: <strong>{totalQuestions}</strong> questions
              </div>
            </div>
          ) : null}

          {/* ── Assignment + Schedule (side by side) ── */}
          <div
            className="row g-3 mt-1 pt-3 align-items-start"
            style={{ borderTop: "1px solid #e9ecef" }}
          >
            {/* Left: Assignment */}
            <div className="col-md-6">
              <Label className="mb-1 fw-semibold" style={{ fontSize: 12 }}>
                Assign To <span className="text-danger">*</span>
              </Label>
              <div className="d-flex gap-3 mb-2" style={{ fontSize: 13 }}>
                {["internal", "external", "department"].map(at => (
                  <label
                    key={at}
                    className="d-flex align-items-center gap-1 text-capitalize fw-normal"
                    style={{ cursor: "pointer", fontSize: 13 }}
                  >
                    <input
                      type="radio"
                      className="form-check-input mt-0"
                      name="assign_type"
                      checked={formData.assign_type === at}
                      onChange={() =>
                        setFormData(prev => ({
                          ...prev,
                          assign_type: at,
                          type: at === "internal" ? 1 : 0,
                          assignees: at !== "internal" ? [] : prev.assignees,
                          departments: at !== "department" ? [] : prev.departments,
                          roles: at !== "department" ? [] : prev.roles,
                        }))
                      }
                    />
                    {at}
                  </label>
                ))}
              </div>

              {formData.assign_type === "internal" && (
                <div>
                  <Label className="mb-1" style={{ fontSize: 12 }}>Members</Label>
                  <Select
                    placeholder="Select members…"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    options={allStaffOptions}
                    value={formData.assignees}
                    onChange={e => handleChange({ target: { name: "assignees", value: e } })}
                    isMulti
                  />
                </div>
              )}
              {formData.assign_type === "department" && (
                <div>
                  <Label className="mb-1" style={{ fontSize: 12 }}>Departments</Label>
                  <Select
                    placeholder="Select departments…"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    options={Departments}
                    value={formData.departments}
                    onChange={e => handleChange({ target: { name: "departments", value: e } })}
                    isMulti
                  />
                  <Label className="mb-1 mt-2" style={{ fontSize: 12 }}>
                    Roles{" "}
                    <span className="text-muted" style={{ fontWeight: 400, fontSize: 11 }}>
                      (optional — leave empty to include all roles)
                    </span>
                  </Label>
                  <Select
                    placeholder="Select roles…"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    options={Roles}
                    value={formData.roles}
                    onChange={e => handleChange({ target: { name: "roles", value: e } })}
                    isMulti
                  />
                </div>
              )}
              {formData.assign_type === "external" && (
                <div>
                  <Label className="mb-1" style={{ fontSize: 12 }}>&nbsp;</Label>
                  <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                    External candidates access via exam link.
                  </p>
                </div>
              )}
            </div>

            {/* Right: Schedule (only for Actual) */}
            {formData.test_type === 0 && (
              <div className="col-md-6">
                <Label className="mb-1 fw-semibold" style={{ fontSize: 12 }}>
                  Schedule
                </Label>
                <div className="d-flex gap-3 mb-2" style={{ fontSize: 13 }}>
                  <label className="d-flex align-items-center gap-1 fw-normal" style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      className="form-check-input mt-0"
                      name="is_recurring"
                      checked={formData.is_recurring === 0}
                      onChange={() =>
                        setFormData(prev => ({ ...prev, is_recurring: 0, recurring_day: [] }))
                      }
                    />
                    One-time
                  </label>
                  <label className="d-flex align-items-center gap-1 fw-normal" style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      className="form-check-input mt-0"
                      name="is_recurring"
                      checked={formData.is_recurring === 1}
                      onChange={() =>
                        setFormData(prev => ({ ...prev, is_recurring: 1, visible_date: "" }))
                      }
                    />
                    Recurring
                  </label>
                </div>

                {formData.is_recurring === 0 ? (
                  <div>
                    <Label className="mb-1" style={{ fontSize: 12 }}>Visible From</Label>
                    <Input

                      type="date"
                      name="visible_date"
                      value={formData.visible_date}
                      onChange={handleChange}
                    />
                    <small className="text-muted" style={{ fontSize: 11 }}>
                      Defaults to today if not set
                    </small>
                  </div>
                ) : (
                  <div>
                    <Label className="mb-1" style={{ fontSize: 12 }}>Repeat on</Label>
                    <div className="d-flex gap-1 flex-wrap">
                      {DAYS.map((day, idx) => {
                        const active = formData.recurring_day?.includes(idx)
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() =>
                              setFormData(prev => ({
                                ...prev,
                                recurring_day: active
                                  ? prev.recurring_day.filter(d => d !== idx)
                                  : [...(prev.recurring_day || []), idx],
                              }))
                            }
                            style={{
                              padding: "8px 10px",
                              fontSize: 12,
                              borderRadius: 5,
                              border: "1px solid",
                              cursor: "pointer",
                              fontWeight: active ? 700 : 400,
                              background: active ? "#752a97" : "#fff",
                              borderColor: active ? "#752a97" : "#ccc",
                              color: active ? "#fff" : "#555",
                            }}
                          >
                            {day}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Submit ── */}
          <div className="mt-3 pt-3" style={{ borderTop: "1px solid #e9ecef" }}>
            <button type="submit" className="border-radius btn btn-purple btn-sm px-4">
              <i className="fas fa-save me-2"></i>
              {isEdit ? "Update" : "Submit"}
            </button>
          </div>
        </Form>
      </div>
    </Modal>
  )
}

function TableContainer({
  columns,
  data,
  setSelectedData,
  selectedData,
  handleSubmit,
  Categories,
  Departments,
  Roles,
  canAdd,
  onAddPermission,
}) {
  const [modal, setModal] = useState(false)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    setGlobalFilter,
    prepareRow,
  } = useTable({ columns, data }, useGlobalFilter, usePagination)

  const { pageIndex, pageSize, globalFilter } = state
  const navigate = useNavigate()

  return (
    <div className="px-2">
      <Row className="mb-2">
        <Col lg={2}>
          <select
            className="border-radius form-select"
            style={{ width: "100px" }}
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {[10, 20, 30, 40, 50].map(ps => (
              <option key={ps} value={ps}>
                Show {ps}
              </option>
            ))}
          </select>
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="me-4">
            <div className="search-box me-xxl-2 my-xxl-0 d-inline-block">
              <div className="position-relative">
                <input
                  onChange={e => setGlobalFilter(e.target.value)}
                  type="text"
                  className="border-radius form-control rounded h-100"
                  placeholder="Search..."
                  value={globalFilter || ""}
                />
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </div>
          </div>
          <div>
            {canAdd && (
              <button className="border-radius btn btn-purple" onClick={onAddPermission}>
                <i className="fas fa-plus me-2 icon-size"></i>Assessment Exam
              </button>
            )}
            <button
              className="border-radius btn text-white ms-md-2 btn-red"
              onClick={() => navigate(-1)}
            >
              <i className="dripicons-cross me-2 icon-center"></i>Cancel
            </button>
          </div>
        </Col>
      </Row>

      <Table className="table table-bordered mt-4" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr key={i} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, index) => (
                <th key={index} className={column.className} {...column.getHeaderProps()}>
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row)
            return (
              <tr className="row-hover" {...row.getRowProps()} key={i}>
                {row.cells.map((cell, index) => (
                  <td className={cell.column.className} key={index} {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </Table>

      <Row className="justify-content-md-end justify-content-center align-items-center">
        <NewPagination
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          previousPage={previousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          pageSize={pageSize}
          page={page}
          data={data}
          nextPage={nextPage}
        />
      </Row>

      <ExamModal
        modal={modal}
        setModal={setModal}
        selectedData={selectedData}
        onSubmit={handleSubmit}
        Categories={Categories}
        Departments={Departments}
        Roles={Roles}
      />
    </div>
  )
}

const AssessmentTestIndex = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [copied, setCopied] = useState(null)
  const [modal, setModal] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [Categories, setCategories] = useState([])
  const [Departments, setDepartments] = useState([])
  const [Roles, setRoles] = useState([])
  const [modal_Access, setAccessDenied] = useState(false)
  const [permissions, setPermissions] = useState({ canAdd: false, canEdit: false, canDelete: false })

  const fetchPermissions = async () => {
    const fd = new FormData()
    fd.append("user_id", localStorage.getItem("auth_id"))
    fd.append("module_id", TEST_MODULE_ID)
    try {
      const res = await axios({
        method: "post",
        url: `${configure.apiUrl}/earth_permission`,
        data: fd,
        headers: { Accept: "application/json", "Content-Type": "multipart/form-data" },
      })
      const india = res.data.data.india
      const uk = res.data.data.uk
      setPermissions({
        canAdd: india[3] == 1 || uk[3] == 1,
        canEdit: india[4] == 1 || uk[4] == 1,
        canDelete: india[5] == 1 || uk[5] == 1,
      })
    } catch (e) {
      console.log(e)
    }
  }

  const checkPermission = async (index, callback) => {
    setLoading(true)
    const fd = new FormData()
    fd.append("user_id", localStorage.getItem("auth_id"))
    fd.append("module_id", TEST_MODULE_ID)
    try {
      const res = await axios({
        method: "post",
        url: `${configure.apiUrl}/earth_permission`,
        data: fd,
        headers: { Accept: "application/json", "Content-Type": "multipart/form-data" },
      })
      if (res.data.data.india[index] == 1 || res.data.data.uk[index] == 1) {
        callback()
      } else {
        setAccessDenied(true)
      }
    } catch (e) {
      console.log(e)
    }
    setLoading(false)
  }

  const handleEdit = row => {
    checkPermission(4, () => {
      setSelectedData(row)
      setModal(true)
    })
  }

  const getCategories = async () => {
    const response = await fetchNodeData(
      null,
      "preassesment/category/getAllCategory",
      secureLocalStorage?.getItem("auth_token")
    )
    setCategories(
      response.data?.map(cate => ({ value: cate.id, label: cate.name })) || []
    )
  }

  const getDepartments = async () => {
    try {
      const res = await axios({
        method: "get",
        url: `${configure.apiUrl}/departments`,
        headers: { Accept: "application/json" },
      })
      const deptList = res.data?.data?.department || []
      setDepartments(deptList.map(d => ({ value: d.id, label: d.department_name })))
    } catch (e) {
      console.log("Departments fetch error", e)
    }
  }

  const getRoles = async () => {
    try {
      const res = await axios({
        method: "get",
        url: `${configure.apiUrl}/staffData`,
        headers: { Accept: "application/json" },
      })
      const roleList = res.data?.data?.roles || []
      setRoles(roleList.map(r => ({ value: r.id, label: r.role_name })))
    } catch (e) {
      console.log("Roles fetch error", e)
    }
  }

  const getExams = async () => {
    const userId = localStorage.getItem("auth_id")
    const response = await fetchNodeData(
      setLoading,
      `preassesment/test/get/${userId}`,
      secureLocalStorage?.getItem("auth_token")
    )
    setData(
      response.data?.map(item => ({
        ...item,
        category: JSON.parse(item.category || "[]"),
        responses: 0,
      })) || []
    )
  }

  useEffect(() => {
    ; (async () => {
      await getExams()
      await getCategories()
      await getDepartments()
      await getRoles()
    })()
    fetchPermissions()
    document.title = "Assessment Tests | Zithas Technologies"
  }, [])

  const handleDelete = id => {
    checkPermission(5, async () => {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      })
      if (result.isConfirmed) {
        const response = await FetchNodeDeleteData(setLoading, "preassesment/test/delete", id)
        if (response?.status === 200) {
          Swal.fire("Deleted!", "Your record has been deleted.", "success")
          getExams()
        } else {
          Swal.fire("Error!", "Something went wrong.", "error")
        }
      }
    })
  }

  const copyLink = async (link, id) => {
    try {
      setCopied(id)
      await navigator.clipboard.writeText(link)
      setTimeout(() => setCopied(null), 1000)
    } catch (err) {
      console.error("Failed to copy: ", err)
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        className: "text-center",
      },
      {
        Header: "Exam Title",
        accessor: "test_name",
        className: "col-3",
        Cell: cellProps => {
          const { test_name, test_type } = cellProps.row.original
          return (
            <span>
              {test_name}
              {test_type === 1 && (
                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: "11px" }}>
                  MOCK
                </span>
              )}
            </span>
          )
        },
      },
      {
        Header: "Categories",
        accessor: "category",
        className: "col-3",
        Cell: cellProps => (
          <div className="d-flex gap-1 flex-wrap">
            {cellProps?.row?.original?.category?.map(item => (
              <span
                key={item.value}
                className="badge bg-white text-dark border"
                style={{ fontSize: "12px" }}
              >
                {item.label}
              </span>
            ))}
          </div>
        ),
      },
      {
        Header: "No. of Questions",
        accessor: "count",
      },
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => formatDate(value) || "—",
      },
      {
        Header: "Action",
        accessor: "action",
        className: "",
        Cell: cellProps => {
          const { id } = cellProps.row.original
          const examUrl = `${window.location.origin}${configure.appUrl}assessment-exam/${encodeTestId(id)}`
          return (
            <div className="d-flex align-items-center gap-1 flex-wrap">
              <Link
                to={`${configure.appUrl}assessment-tests/responses/${id}`}
                className="btn btn-sm btn-outline-success"
                title="View Responses"
              >
                <i className="fas fa-user me-1"></i>Responses
              </Link>
              <button
                onClick={() => copyLink(examUrl, id)}
                className="btn btn-sm btn-outline-primary"
                title="Copy exam link"
              >
                <i className={`fas fa-${copied == id ? "check" : "link"}`}></i>
              </button>
              {permissions.canEdit && (
                <button
                  onClick={() => handleEdit(cellProps?.row?.original)}
                  className="btn btn-sm btn-outline-secondary"
                  title="Edit"
                >
                  <i className="fas fa-pencil-alt"></i>
                </button>
              )}
              {permissions.canDelete && (
                <button
                  onClick={() => handleDelete(cellProps?.row?.original?.id)}
                  className="btn btn-sm btn-danger"
                  title="Delete"
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              )}
            </div>
          )
        },
      },
    ],
    [copied, permissions]
  )

  const filteredData = useMemo(() =>
    (data || [])
      .filter(item => Number(item.test_type) === activeTab)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    [data, activeTab]
  )

  async function handleSubmit(formData, totalQuestions, isEdit, setPopup) {
    const submitData = {
      name: formData.title,
      count: totalQuestions,
      category: JSON.stringify(formData.category),
      type: formData.type,
      assignees:
        formData.assign_type === "internal"
          ? formData.assignees.map(i => i.value).toString()
          : null,
      user_id: localStorage.getItem("auth_id"),
      test_type: formData.test_type,
      assign_type: formData.assign_type,
      departments:
        formData.assign_type === "department"
          ? formData.departments.map(d => d.value).join(",") || null
          : null,
      roles:
        formData.assign_type === "department" && formData.roles.length
          ? formData.roles.map(r => r.value).join(",")
          : null,
      is_recurring: formData.is_recurring,
      recurring_day:
        formData.is_recurring && formData.recurring_day.length
          ? formData.recurring_day.join(",")
          : null,
      visible_date: formData.is_recurring === 0 ? (formData.visible_date || null) : null,
    }

    const warningMsg = isEdit ? "You really want to update?" : "You really want to add?"

    swal({
      title: "Are you sure?",
      text: warningMsg,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async willDelete => {
      if (willDelete) {
        try {
          let response
          if (isEdit && formData.id) {
            response = await FetchNodePutData(
              setLoading,
              "preassesment/test/edit",
              formData.id,
              submitData
            )
          } else {
            response = await FetchNodePostData(
              setLoading,
              "preassesment/test/add",
              secureLocalStorage?.getItem("auth_token"),
              submitData
            )
          }
          if (response.data.success) {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: `Exam ${isEdit ? "updated" : "added"} successfully`,
            })
            getExams()
            setPopup(false)
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error)
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to save exam. Please try again.",
          })
        }
      }
    })
  }

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content mb-4">
        <Container fluid>
          <div className="px-2">
            <Breadcrumbs title="Dashboard" breadcrumbItem="Assessment Tests" />
          </div>
          <Card>
            <CardBody>
              {/* Actual / Mock tabs */}
              <ul className="nav nav-tabs mb-3" style={{ borderBottom: "2px solid #e9ecef" }}>
                {[{ label: "Actual Test", value: 0 }, { label: "Mock Test", value: 1 }].map(tab => (
                  <li key={tab.value} className="nav-item">
                    <button
                      className={`nav-link ${activeTab === tab.value ? "active fw-semibold" : "text-muted"}`}
                      style={{
                        border: "none",
                        borderBottom: activeTab === tab.value ? "3px solid #752a97" : "3px solid transparent",
                        color: activeTab === tab.value ? "#752a97" : undefined,
                        background: "transparent",
                        padding: "8px 20px",
                        fontSize: 14,
                      }}
                      onClick={() => setActiveTab(tab.value)}
                    >
                      {tab.label}
                      <span
                        className="ms-2 badge"
                        style={{
                          background: activeTab === tab.value ? "#752a97" : "#e9ecef",
                          color: activeTab === tab.value ? "#fff" : "#555",
                          fontSize: 11,
                        }}
                      >
                        {(data || []).filter(d => Number(d.test_type) === tab.value).length}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              <div className="responsive-table">
                <TableContainer
                  columns={columns}
                  data={filteredData}
                  setLoading={setLoading}
                  setSelectedData={setSelectedData}
                  selectedData={selectedData}
                  handleSubmit={handleSubmit}
                  Categories={Categories}
                  Departments={Departments}
                  Roles={Roles}
                  canAdd={permissions.canAdd}
                  onAddPermission={() =>
                    checkPermission(3, () => {
                      setSelectedData(null)
                      setModal(true)
                    })
                  }
                />
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>

      <ExamModal
        modal={modal}
        setModal={setModal}
        selectedData={selectedData}
        onSubmit={handleSubmit}
        Categories={Categories}
        Departments={Departments}
        Roles={Roles}
      />

      <AccessDeniedPopup
        modal_Access={modal_Access}
        setAccessDenied={setAccessDenied}
      />
    </React.Fragment>
  )
}

export default AssessmentTestIndex
